import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema, softwareApplicationSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, Star, Package, Zap, Layers, ShoppingBag, Shirt, Coffee } from "lucide-react";
import { Link } from "react-router-dom";

const AIProductMockupGenerator = () => {
  const faqs = [
    {
      question: "What types of product mockups can I create?",
      answer: "Generate mockups for t-shirts, mugs, posters, phone cases, packaging, bottles, books, and more. Simply describe your product and desired scene, and our AI creates photorealistic mockups."
    },
    {
      question: "Are these mockups suitable for print-on-demand?",
      answer: "Yes! Our mockups are perfect for POD platforms like Printful, Printify, and Merch by Amazon. Generate product previews before committing to inventory."
    },
    {
      question: "Can I add my own designs to the mockups?",
      answer: "Currently, our AI generates complete mockups based on your description. For adding existing designs to mockups, use our prompt to describe the design you want displayed on the product."
    },
    {
      question: "What resolution are the mockups?",
      answer: "Generate high-resolution mockups suitable for e-commerce listings, social media, and marketing materials. Enhanced options available for print-quality outputs."
    },
    {
      question: "How do I create lifestyle mockups?",
      answer: "Describe the scene in your prompt: 'white t-shirt on a model in a coffee shop' or 'mug on a wooden desk with morning light'. Our AI understands context and creates realistic scenes."
    }
  ];

  const features = [
    {
      icon: <Package className="w-6 h-6" />,
      title: "Any Product Type",
      description: "T-shirts, mugs, packaging, tech accessories, and more."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Results",
      description: "Generate mockups in seconds, not hours of Photoshop work."
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Lifestyle Scenes",
      description: "Products in context - on models, in settings, being used."
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "E-commerce Ready",
      description: "Perfect for Amazon, Etsy, Shopify product listings."
    }
  ];

  const productTypes = [
    "T-Shirts & Apparel",
    "Mugs & Drinkware",
    "Phone Cases",
    "Posters & Prints",
    "Packaging & Boxes",
    "Bottles & Containers",
    "Books & Covers",
    "Tech Accessories"
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="AI Product Mockup Generator | Create Professional Mockups Instantly"
        description="Generate photorealistic product mockups with AI. T-shirts, mugs, packaging & more. Perfect for POD, e-commerce, and marketing. No Photoshop needed. Try free!"
        keywords="ai product mockup generator, mockup generator ai, t-shirt mockup ai, product photography ai, print on demand mockup, ecommerce mockup generator, packaging mockup ai, bottle mockup generator"
        url="/ai-product-mockup-generator"
        jsonLd={[
          organizationSchema,
          softwareApplicationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'AI Product Mockup Generator', url: '/ai-product-mockup-generator' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                18,000+ Product Mockups Generated Monthly
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                AI Product Mockup Generator
                <span className="block text-primary mt-2">Photorealistic Mockups Instantly</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create stunning product mockups without Photoshop. T-shirts, mugs, packaging - 
                any product, any scene. Perfect for POD and e-commerce.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild size="lg" className="text-lg px-8">
                  <Link to="/try-generator">Generate Free Mockup</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8">
                  <Link to="/gallery">View Examples</Link>
                </Button>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> No Photoshop needed
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> POD ready
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Commercial rights
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Sellers Love AI Mockups</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional product visuals without the professional costs.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <div key={index} className="bg-card p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Types */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Mockups for Every Product</h2>
                <p className="text-muted-foreground">
                  From apparel to packaging, create mockups for any product type
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {productTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-2 bg-muted/50 p-4 rounded-lg">
                    <Shirt className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm font-medium">{type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
              <div>
                <div className="text-4xl font-bold mb-2">18K+</div>
                <div className="text-primary-foreground/80">Mockups Created</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">50+</div>
                <div className="text-primary-foreground/80">Product Types</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">&lt;15s</div>
                <div className="text-primary-foreground/80">Generation Time</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">$0</div>
                <div className="text-primary-foreground/80">To Start</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Create Product Mockups?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join thousands of POD sellers and e-commerce brands using AI mockups.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Create Mockups Free</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AIProductMockupGenerator;
